'use client';

import Typography from '@mui/material/Typography';
import { Button } from '@startengine/design-system.ui.button';
import { RightArrowIcon } from '@startengine/design-system.ui.icons';
import { colors } from '@startengine/design-system.theme.main';
import PropTypes from 'prop-types';

import { usePathname } from 'next/navigation';
import { BannerGrid } from './styles';
import { DesktopBannerButtonCopy, PromoButton } from 'components/Homepage/styles';
import { getPrimaryEnv } from 'utils/helpers';
import useIsMobile from 'hooks/useIsMobile';

const OFFERINGS = {
  hylio: {
    href: `${getPrimaryEnv()}/offering/hylio`,
    buttonText: 'Invest Now',
    bannerText: `NOW LIVE: Invest in Hylio Inc.'s latest round. See detailed disclosures on page.`,
  },
  default: {
    href: `${getPrimaryEnv()}/explore`,
    buttonText: 'Explore Investments',
    bannerText:
      'This offering is no longer accepting investments. Browse other offerings currently listed on StartEngine. Availability and eligibility vary.',
  },
};

const BannerButton = ({ offeringType = 'default' }) => {
  const offeringData = OFFERINGS[offeringType] || OFFERINGS.default;

  return (
    <PromoButton
      component="a"
      href={offeringData.href}
      variant="outlined"
      color="success"
      aria-label={offeringData.buttonText}
      data-cy="closed-banner-button"
    >
      <DesktopBannerButtonCopy variant="p2Bold">{offeringData.buttonText}</DesktopBannerButtonCopy>
    </PromoButton>
  );
};

BannerButton.propTypes = {
  offeringType: PropTypes.oneOf(['hylio', 'default']),
};

const BannerButtonMobile = ({ offeringType = 'default' }) => {
  const offeringData = OFFERINGS[offeringType] || OFFERINGS.default;

  return (
    <Button
      component="a"
      href={offeringData.href}
      icon
      aria-label={offeringData.buttonText}
      name={offeringData.buttonText}
      data-cy="closed-banner-button-mobile"
      sx={{ alignSelf: 'center', textDecoration: 'none' }}
    >
      <RightArrowIcon sx={{ height: '34px', width: '34px' }} />
    </Button>
  );
};

BannerButtonMobile.propTypes = {
  offeringType: PropTypes.oneOf(['hylio', 'default']),
};

const ClosedBanner = () => {
  const isMobile = useIsMobile();
  const pathname = usePathname();

  const getOfferingType = () => {
    if (pathname?.includes('hylio-inc')) return 'hylio';
    return 'default';
  };

  const offeringType = getOfferingType();
  const offeringData = OFFERINGS[offeringType] || OFFERINGS.default;

  if (!offeringData) {
    return null;
  }

  return (
    <BannerGrid
      color={colors.Red2}
      sx={{
        position: { xs: 'relative' },
        zIndex: 999,
      }}
      data-cy="closed-banner"
      data-testid="closed-banner"
    >
      <Typography
        sx={{
          fontSize: { xs: '12px', sm: '20px' },
          typography: { xs: 'p2Medium', sm: 'p1Bold' },
        }}
      >
        {offeringData.bannerText}
      </Typography>
      {isMobile ? (
        <BannerButtonMobile offeringType={offeringType} />
      ) : (
        <BannerButton offeringType={offeringType} />
      )}
    </BannerGrid>
  );
};

export default ClosedBanner;
